console.log('🔴 [DiallerDropdown] FILE LOADED');

import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useBusinessId } from '../hooks/use-business-id';
import { useCall } from '../contexts/CallContext';
import { Phone, Delete, X, BookOpen } from 'lucide-react';
import { createPortal } from 'react-dom';
import SelectCustomerModal from './manage/SelectCustomerModal';
import { Customer } from '../lib/customers';

interface DiallerDropdownProps {
  className?: string;
  unviewedMissedCount?: number;
}

export const DiallerDropdown: React.FC<DiallerDropdownProps> = ({ className = '', unviewedMissedCount = 0 }) => {
  const businessId = useBusinessId();
  
  let callContext;
  try {
    callContext = useCall();
  } catch (error) {
    console.error('[DiallerDropdown] useCall() ERROR:', error);
    throw error;
  }
  
  const { startCall, deviceStatus, errorMessage: contextErrorMessage, activeCallState, maximizeCall } = callContext;
  const [isOpen, setIsOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [matchedCustomer, setMatchedCustomer] = useState<{id: string, first_name: string, last_name: string} | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isSelectCustomerModalOpen, setIsSelectCustomerModalOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Lookup customer by phone number
  const lookupCustomerByNumber = async (number: string) => {
    if (!businessId || !number || number.length < 3) {
      setMatchedCustomer(null);
      return;
    }

    try {
      const normalizedNumber = number.replace(/\D/g, '');
      const searchPatterns = [
        number,
        normalizedNumber,
        `+1${normalizedNumber}`,
        `+${normalizedNumber}`,
        `1${normalizedNumber}`
      ];

      const { data: customers, error } = await supabase
        .from('customers')
        .select('id, first_name, last_name, phone')
        .eq('business_id', businessId);

      if (error || !customers) {
        setMatchedCustomer(null);
        return;
      }

      const matchedCustomer = customers.find(customer => {
        if (!customer.phone) return false;
        const customerNormalized = customer.phone.replace(/\D/g, '');
        return searchPatterns.some(pattern => {
          const patternNormalized = pattern.replace(/\D/g, '');
          return patternNormalized === customerNormalized ||
                 patternNormalized === customerNormalized.substring(1) ||
                 `1${patternNormalized}` === customerNormalized;
        });
      });

      if (matchedCustomer) {
        setMatchedCustomer({
          id: matchedCustomer.id,
          first_name: matchedCustomer.first_name,
          last_name: matchedCustomer.last_name
        });
      } else {
        setMatchedCustomer((prev) => (prev?.id ? prev : null));
      }
    } catch (err) {
      setMatchedCustomer((prev) => (prev?.id ? prev : null));
    }
  };

  // Listen for custom event to open dialler with phone number
  useEffect(() => {
    const handleOpenDialler = (event: CustomEvent<{ phoneNumber: string; customerId?: string; firstName?: string; lastName?: string }>) => {
      const number = event.detail?.phoneNumber;
      if (!number) return;
      // Format the phone number - remove all non-digits first
      const digits = String(number).replace(/\D/g, '');
      if (!digits) return;
      setPhoneNumber(digits);
      setErrorMessage('');
      if (event.detail?.customerId) {
        setMatchedCustomer({
          id: event.detail.customerId,
          first_name: event.detail.firstName || '',
          last_name: event.detail.lastName || '',
        });
      }
      // Wait for stacked modals (customer details, booking, etc.) to finish closing
      window.setTimeout(() => {
        setIsOpen(true);
      }, 280);
    };

    window.addEventListener('openDialler' as any, handleOpenDialler as EventListener);
    
    return () => {
      window.removeEventListener('openDialler' as any, handleOpenDialler as EventListener);
    };
  }, []);

  // Lookup customer when phone number changes
  useEffect(() => {
    if (isOpen && phoneNumber.length >= 3) {
      lookupCustomerByNumber(phoneNumber);
    } else {
      setMatchedCustomer(null);
    }
  }, [phoneNumber, isOpen, businessId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Don't close if clicking inside the dropdown
      if (dropdownRef.current && dropdownRef.current.contains(target)) {
        return;
      }
      
      // Don't close if clicking on the button
      if (buttonRef.current && buttonRef.current.contains(target)) {
        return;
      }
      
      // Don't close if clicking inside the SelectCustomerModal
      const selectCustomerModal = document.querySelector('[data-select-customer-modal]');
      if (selectCustomerModal && selectCustomerModal.contains(target)) {
        return;
      }
      
      // Don't close if the customer modal is open
      if (isSelectCustomerModalOpen) {
        return;
      }
      
      setIsOpen(false);
    };

    if (isOpen) {
      // Use a small delay to avoid closing immediately when opening
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
      }, 100);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }
  }, [isOpen, isSelectCustomerModalOpen]);

  // Add digit to phone number
  const addDigit = (digit: string) => {
    setPhoneNumber(prev => prev + digit);
  };

  // Handle keypad button click
  const handleKeypadClick = (digit: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addDigit(digit);
  };

  // Remove last digit
  const removeDigit = () => {
    setPhoneNumber(prev => prev.slice(0, -1));
  };

  // Make a call using the context
  const handleMakeCall = async () => {
    if (!phoneNumber.trim()) {
      setErrorMessage('Please enter a phone number');
      return;
    }
    
    if (deviceStatus !== 'ready') {
      setErrorMessage('Device is not ready. Please wait for initialization.');
      return;
    }
    
    try {
      // Start call through context
      await startCall(
        phoneNumber,
        matchedCustomer ? {
          id: matchedCustomer.id,
          first_name: matchedCustomer.first_name,
          last_name: matchedCustomer.last_name
        } : undefined
      );
      
      // Close the dropdown after starting call
      setIsOpen(false);
      setPhoneNumber('');
      setMatchedCustomer(null);
      setErrorMessage('');
    } catch (error: any) {
      setErrorMessage(error?.message || 'Failed to start call');
    }
  };

  // Get dropdown position
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);

  useEffect(() => {
    if (isOpen) {
      // Use a small delay to ensure buttonRef is available (especially after modals close)
      const calculatePosition = () => {
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          const isMobile = viewportWidth < 768;
          
          // Use full width on mobile, or a wider fixed width on desktop
          const dropdownWidth = isMobile 
            ? viewportWidth - 16 // Full width minus margins on mobile
            : Math.min(400, viewportWidth - 32); // Max 400px on desktop with margins
          
          // Position dropdown - center it below the button
          const buttonCenter = rect.left + (rect.width / 2);
          let left = buttonCenter - (dropdownWidth / 2);
          
          // Ensure it doesn't go off screen
          if (left < 8) {
            left = 8; // Minimum margin from screen edge
          } else if (left + dropdownWidth > viewportWidth - 8) {
            left = viewportWidth - dropdownWidth - 8; // Align to right edge with margin
          }
          
          setDropdownPosition({
            top: rect.bottom + 8,
            left: left,
            width: dropdownWidth
          });
        } else {
          // Retry if buttonRef not ready yet
          setTimeout(calculatePosition, 50);
        }
      };
      
      // Initial calculation
      calculatePosition();
      
      // Also recalculate on window resize
      const handleResize = () => calculatePosition();
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    } else {
      setDropdownPosition(null);
    }
  }, [isOpen]);

  // Combine error messages
  const displayError = errorMessage || contextErrorMessage;

  const hasOngoingCall = activeCallState.callStatus !== 'idle' && activeCallState.isMinimized;

  const handleButtonClick = () => {
    if (hasOngoingCall) {
      maximizeCall();
    } else {
      setIsOpen(!isOpen);
    }
  };

  return (
    <>
      <div className={`relative ${className}`}>
        {/* Dialler Button — shows ongoing call indicator when a call is minimized */}
        <button
          ref={buttonRef}
          onClick={handleButtonClick}
          className={`relative flex items-center space-x-2 transition-all duration-300 ease-out p-2 rounded-xl transform hover:scale-105 active:scale-95 touch-manipulation ${
            hasOngoingCall
              ? 'text-green-400 hover:text-green-300 bg-green-500/20 hover:bg-green-500/30'
              : 'text-white/90 hover:text-white hover:bg-white/15'
          }`}
          title={hasOngoingCall ? 'Return to call' : 'Dial Pad'}
          data-assistant-anchor="dialler-button"
        >
          {hasOngoingCall ? (
            <Phone className="w-5 h-5 animate-pulse" />
          ) : (
            <div className="w-5 h-5 grid grid-cols-3 gap-0.5">
              <div className="w-1 h-1 bg-current rounded-full"></div>
              <div className="w-1 h-1 bg-current rounded-full"></div>
              <div className="w-1 h-1 bg-current rounded-full"></div>
              <div className="w-1 h-1 bg-current rounded-full"></div>
              <div className="w-1 h-1 bg-current rounded-full"></div>
              <div className="w-1 h-1 bg-current rounded-full"></div>
              <div className="w-1 h-1 bg-current rounded-full"></div>
              <div className="w-1 h-1 bg-current rounded-full"></div>
              <div className="w-1 h-1 bg-current rounded-full"></div>
            </div>
          )}
          
          {/* Badge: ongoing call green dot OR missed call count */}
          {hasOngoingCall ? (
            <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-gray-900 animate-pulse" />
          ) : unviewedMissedCount > 0 ? (
            <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center font-medium shadow-lg">
              {unviewedMissedCount > 9 ? '9+' : unviewedMissedCount}
            </span>
          ) : null}
        </button>
      </div>

      {/* Dropdown Panel - Portal to body for proper z-index */}
      {isOpen && dropdownPosition && createPortal(
        <div 
          ref={dropdownRef}
          className="fixed bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 z-[100300] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            maxWidth: `${dropdownPosition.width}px`,
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            animation: 'iosSlideDown 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
          }}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200/50 flex items-center justify-between relative">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900">Dialler</h3>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsSelectCustomerModalOpen(true);
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Select customer from address book"
              >
                <BookOpen className="h-4 w-4" />
              </button>
            </div>
            <div className="flex items-center gap-2">
              {/* Status Indicator Dot */}
              <div className="w-3 h-3 rounded-full shadow-sm" style={{
                backgroundColor: deviceStatus === 'ready' ? '#10b981' :
                                deviceStatus === 'initializing' ? '#eab308' :
                                deviceStatus === 'not_configured' ? '#9ca3af' :
                                '#ef4444'
              }}></div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {deviceStatus === 'not_configured' ? (
            <div className="px-6 py-10 text-center">
              <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <Phone className="w-6 h-6 text-gray-400" />
              </div>
              <h4 className="text-base font-semibold text-gray-900 mb-1">Connect Twilio for calling</h4>
              <p className="text-sm text-gray-500 mb-4">Set up Twilio in your business settings to make and receive calls.</p>
              <button
                onClick={() => { setIsOpen(false); window.location.href = '/app-store?tab=integrations'; }}
                className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
              >
                Go to Settings
              </button>
            </div>
          ) : (
            <>
              {/* Phone Number Display */}
              <div className="px-4 py-4 border-b border-gray-200/50">
                <div className="text-center">
                  {phoneNumber ? (
                    <div>
                      <div className="text-gray-900 text-2xl font-light tracking-wider">
                        {phoneNumber}
                      </div>
                      {matchedCustomer && (
                        <div className="text-blue-600 text-sm font-medium mt-1">
                          {`${matchedCustomer.first_name} ${matchedCustomer.last_name}`.trim()}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400 text-lg">Enter number</span>
                  )}
                </div>
              </div>

              {/* Keypad */}
              <div className="p-4">
                <div className="grid grid-cols-3 gap-3 mb-4 justify-items-center">
                  {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map((key) => (
                    <button
                      key={key}
                      onClick={(e) => handleKeypadClick(key, e)}
                      className="h-14 w-14 rounded-full bg-gray-100 hover:bg-gray-200 active:bg-gray-300 flex items-center justify-center text-2xl font-light text-gray-900 transition-all duration-150 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                      type="button"
                    >
                      {key}
                    </button>
                  ))}
                </div>

                {/* Bottom Controls */}
                <div className="flex items-center justify-center gap-6">
                  {/* Delete Button */}
                  <button
                    onClick={removeDigit}
                    disabled={!phoneNumber}
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                      phoneNumber
                        ? 'text-gray-700 hover:bg-gray-200' 
                        : 'text-gray-400'
                    }`}
                  >
                    <Delete className="w-5 h-5" />
                  </button>

                  {/* Call Button */}
                  <button
                    onClick={handleMakeCall}
                    disabled={!phoneNumber.trim() || deviceStatus !== 'ready'}
                    className={`w-16 h-16 rounded-full flex items-center justify-center transition-all ${
                      phoneNumber.trim() && deviceStatus === 'ready'
                        ? 'bg-green-500 hover:bg-green-600 text-white shadow-lg'
                        : 'bg-gray-300 text-gray-500'
                    }`}
                  >
                    <Phone className="w-6 h-6" />
                  </button>

                  {/* Placeholder for symmetry */}
                  <div className="w-12 h-12"></div>
                </div>

                {/* Error Message */}
                {displayError && (
                  <div className="mt-3 text-center text-sm text-red-600 space-y-1">
                    <div className="font-medium">{displayError}</div>
                    {deviceStatus === 'error' && (
                      <div className="text-xs text-gray-500 mt-1">
                        Check browser console for details
                      </div>
                    )}
                  </div>
                )}

                {/* Device Status */}
                {deviceStatus === 'initializing' && (
                  <div className="mt-3 text-center text-xs text-gray-500">
                    Initializing device...
                  </div>
                )}
                {deviceStatus === 'error' && !displayError && (
                  <div className="mt-3 text-center text-xs text-red-600">
                    Device error - Check console
                  </div>
                )}
              </div>
            </>
          )}
        </div>,
        document.body
      )}

      {/* Select Customer Modal */}
      <SelectCustomerModal
        isOpen={isSelectCustomerModalOpen}
        onClose={() => setIsSelectCustomerModalOpen(false)}
        onSelect={(customer: Customer) => {
          if (customer.phone) {
            // Format phone number - remove all non-digits
            const digits = customer.phone.replace(/\D/g, '');
            setPhoneNumber(digits);
            // Lookup will happen automatically via useEffect
          }
          setIsSelectCustomerModalOpen(false);
        }}
      />
    </>
  );
};
